/**
 * §3 — "last cycle", and the page's one policy fold.
 *
 * `client/widget-*.html` §3 makes one point: usage always bills a month behind, so
 * the month you are looking at is not the month you are being charged for. Every
 * figure comes from `summary.last_period`, which the read model already computed
 * (`WidgetSummarySchema`); nothing here subtracts, multiplies or counts.
 *
 * Two departures from the mockups, both recorded in the report:
 *
 * Months are spelled long throughout — "July 2026", "August 2026" — because both
 * mockups do ("July's conversations were billed at $21.60") and because the rest of
 * this page now does. Two spellings of one month on one screen is the kind of drift
 * a shared `format.month` makes easy and nobody notices.
 *
 *   - **The invoice is not named.** Both pages print `FES-2026-047`, and
 *     `last_period` carries no invoice reference — only `closed`, which says the
 *     month became a line. So the sentence names the month and the link goes to
 *     billing, which is the page that owns invoice numbers anyway (P2).
 *   - **"past the included 1,000" loses its number.** The allowance is stated once
 *     on this page, by §2's meter, and `last_period` has no allowance of its own —
 *     restating this month's against last month's figures would be wrong the first
 *     time a package changed.
 *
 * **A widget not charged per unit** (session 46c — the blog, the forms) bills nothing
 * for what it counted, so its last cycle says so, and its policy fold says what it
 * does cost: nothing, or its monthly fee and the rule it bills by. It has no cap, so
 * the fold sends nobody to Settings for one.
 */

import { isMetered, type WidgetSummaryView } from '@fareast/shared'
import { Link } from '@tanstack/react-router'

import { Empty } from '../../../components/portal/Disclosure.js'
import { Region } from '../../../components/portal/Region.js'
import { Section, SectionHead } from '../../../components/portal/Layout.js'
import { Answer, B, More, Muted, Num, Prose, Small } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'
import { pluralOf } from './text.js'

export type WidgetLastCycleProps = {
  readonly summary: WidgetSummaryView
}

export function WidgetLastCycle({ summary }: WidgetLastCycleProps) {
  const format = useFormat()
  const plural = pluralOf(summary.unit_noun)
  const last = summary.last_period
  const metered = isMetered(summary.unit_amount.amount)

  return (
    <Section>
      <SectionHead title="Last cycle" />

      <Region>
        {last === null ? (
          <Empty>
            There is no earlier month for this widget — {format.monthLong(summary.period)} is the
            first one with anything to show.
          </Empty>
        ) : !metered ? (
          <Answer>
            <Num value={last.used} /> {plural} were recorded in {format.monthLong(last.period)}, and
            none of them was charged.
          </Answer>
        ) : (
          <>
            <Answer>
              {format.monthLong(last.period)}’s {plural} were billed at{' '}
              <B>{format.money(last.amount)}</B>.
            </Answer>

            <Prose>
              <Num value={last.used} /> {plural} were recorded in {format.monthLong(last.period)};{' '}
              <Num value={last.billable} /> of them were past the allowance.{' '}
              {last.closed ? 'That went' : 'That goes'} onto the {format.monthLong(summary.period)}{' '}
              invoice as a single line — the whole charge for this widget, for that month.
            </Prose>

            <Small>
              <Muted>
                <More asChild>
                  <Link to="/app/billing">
                    The {format.monthLong(summary.period)} invoice, line by line →
                  </Link>
                </More>
              </Muted>
            </Small>
          </>
        )}
      </Region>
    </Section>
  )
}
